import { useEffect, useMemo, useRef, useState } from 'react';
import { createGeneration, getPreview, getRemainingUsage, isApiErrorCode } from '../lib/api';
import {
  clothingOptions,
  demoImages,
  moodOptions,
  processingSlides,
  sampleAsset,
} from '../lib/samples';
import { ChoiceCard } from '../components/ChoiceCard';
import { FileDrop } from '../components/FileDrop';
import { Reviews } from '../components/Reviews';
import { getClientId, isNewsletterTestRequested } from '../lib/usage';
import { getConfig } from '../lib/config';
import { readActiveRoot, rememberActiveRoot } from '../lib/active-root';
import { estimatePortraitProgress, PortraitProgressStage } from '../lib/progress';
import { NewsletterModal } from '../components/NewsletterModal';

const MAX_SUBJECTS_PER_GROUP = 5;

function isFile(value: File | undefined): value is File {
  return value instanceof File;
}

async function canLoadImage(url: string) {
  return new Promise<boolean>((resolve) => {
    const image = new Image();
    const timer = window.setTimeout(() => {
      image.onload = null;
      image.onerror = null;
      resolve(false);
    }, 8000);

    image.onload = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
    image.onerror = () => {
      window.clearTimeout(timer);
      resolve(false);
    };
    image.src = url;
  });
}

function ProcessingView({ message, progress }: { message: string; progress: number }) {
  const [activeStory, setActiveStory] = useState(0);

  const goToStory = (index: number) => {
    const nextIndex = (index + processingSlides.length) % processingSlides.length;
    setActiveStory(nextIndex);
  };

  return (
    <div className="upload-flow processing-flow">
      <header className="hero-copy">
        <h1>
          Together in a Moment
          <br />
          That Lasts <em>Forever</em>
        </h1>
      </header>

      <div className="processing-box">
        <div className="processing-copy">
          <span>{message}</span>
        </div>
        <div className="progress-track" aria-label="Portrait generation progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} role="progressbar">
          <span style={{ width: `${progress}%` }} />
        </div>
        <strong className="processing-percent">{progress}%</strong>
        <small className="processing-remaining">We will keep checking until your portrait is ready.</small>
      </div>

      <p className="processing-note">
        Your portrait is being prepared! Please keep this page open while it comes to life
      </p>

      <section className="processing-slider-section">
        <div className="processing-slider">
          <button
            type="button"
            className="processing-slider-nav processing-slider-prev"
            aria-label="Previous testimonial"
            onClick={() => goToStory(activeStory - 1)}
          >
            <span aria-hidden="true">&lsaquo;</span>
          </button>
          <div className="processing-slider-viewport">
            <div className="processing-slider-track" style={{ transform: `translateX(-${activeStory * 100}%)` }}>
              {processingSlides.map((slide) => (
                <article className="processing-slide" key={slide.author}>
                  <div className="processing-slide-copy">
                    <div className="processing-stars" aria-label="5 star review">
                      <span>★</span>
                      <span>★</span>
                      <span>★</span>
                      <span>★</span>
                      <span>★</span>
                    </div>
                    <p>"{slide.text}"</p>
                    <strong>– {slide.author}</strong>
                  </div>
                  <div className="processing-slide-image">
                    <img alt={slide.author} src={slide.image} loading="lazy" />
                  </div>
                </article>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="processing-slider-nav processing-slider-next"
            aria-label="Next testimonial"
            onClick={() => goToStory(activeStory + 1)}
          >
            <span aria-hidden="true">&rsaquo;</span>
          </button>
          <div className="processing-slider-pagination" aria-hidden="true">
            {processingSlides.map((slide, index) => (
              <span className={index === activeStory ? 'is-active' : ''} key={slide.author} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function previewPath(id: string, token?: string, newsletterTest?: boolean) {
  const params = new URLSearchParams({ preview: id });
  if (token) params.set('token', token);
  if (newsletterTest) params.set('newsletterTest', '1');
  return `/?${params.toString()}`;
}

function rememberAccessToken(id: string, token?: string) {
  if (!id || !token) return;
  window.sessionStorage.setItem(`portrait_access_token:${id}`, token);
}

function readUploadRoot() {
  return new URLSearchParams(window.location.search).get('uploadRoot') || readActiveRoot()?.rootId || '';
}

function readUploadRootToken(rootId: string) {
  if (!rootId) return '';
  return (
    new URLSearchParams(window.location.search).get('token') ||
    window.sessionStorage.getItem(`portrait_access_token:${rootId}`) ||
    readActiveRoot()?.token ||
    ''
  );
}

function DownloadView({
  error,
  message,
  onDownloadAgain
}: {
  error?: string;
  message: string;
  onDownloadAgain: () => void;
}) {
  return (
    <div className="download-flow">
      <div className="download-card">
        <span className="download-kicker">Portrait download</span>
        <h1>Your portrait file</h1>
        <p>{message}</p>
        {error && <p className="form-error">{error}</p>}
        <button type="button" className="primary-action" onClick={onDownloadAgain}>
          Download again
        </button>
      </div>
    </div>
  );
}

export function UploadPage() {
  const downloadId = useMemo(() => new URLSearchParams(window.location.search).get('download'), []);
  const uploadRootId = useMemo(readUploadRoot, []);
  const uploadRootToken = useMemo(() => readUploadRootToken(uploadRootId), [uploadRootId]);
  const [humanImages, setHumanImages] = useState<Array<File | undefined>>([undefined]);
  const [petImages, setPetImages] = useState<Array<File | undefined>>([undefined]);
  const [mood, setMood] = useState(moodOptions[0].id);
  const [clothingStyle] = useState(clothingOptions[0].id);
  const [isSubmitting, setSubmitting] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingStage, setProcessingStage] = useState<PortraitProgressStage>('analyzing');
  const [processingStartedAt, setProcessingStartedAt] = useState<number>();
  const [progressTick, setProgressTick] = useState(0);
  const [error, setError] = useState('');
  const [downloadMessage, setDownloadMessage] = useState('');
  const [remainingUses, setRemainingUses] = useState<number>();
  const [dailyLimit, setDailyLimit] = useState<number>();
  const [requiresSubscription, setRequiresSubscription] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const demoStripRef = useRef<HTMLDivElement>(null);
  const config = getConfig();
  const clientId = useMemo(() => getClientId(), []);
  const newsletterTest = useMemo(isNewsletterTestRequested, []);
  const numberOfPersons = humanImages.length;
  const numberOfPets = petImages.length;

  const hasRemainingUses = remainingUses === undefined || remainingUses > 0;
  const hasRequiredUploads =
    humanImages.every(isFile) && petImages.every(isFile);
  const canSubmit = hasRequiredUploads && !isSubmitting && hasRemainingUses;

  function setHumanImageAt(index: number, file: File) {
    setHumanImages((current) => {
      const next = [...current];
      next[index] = file;
      return next;
    });
  }

  function setPetImageAt(index: number, file: File) {
    setPetImages((current) => {
      const next = [...current];
      next[index] = file;
      return next;
    });
  }

  function removeHumanImageAt(index: number) {
    setHumanImages((current) => current.length === 1 ? [undefined] : current.filter((_, position) => position !== index));
  }

  function removePetImageAt(index: number) {
    setPetImages((current) => current.length === 1 ? [undefined] : current.filter((_, position) => position !== index));
  }

  function addHumanImage() {
    setHumanImages((current) => current.length < MAX_SUBJECTS_PER_GROUP && current.every(isFile) ? [...current, undefined] : current);
  }

  function addPetImage() {
    setPetImages((current) => current.length < MAX_SUBJECTS_PER_GROUP && current.every(isFile) ? [...current, undefined] : current);
  }

  useEffect(() => {
    if (!isSubmitting || !processingStartedAt) return;
    const timer = window.setInterval(() => setProgressTick((tick) => tick + 1), 1000);
    return () => window.clearInterval(timer);
  }, [isSubmitting, processingStartedAt]);

  useEffect(() => {
    if (!isSubmitting || !processingStartedAt) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isSubmitting, processingStartedAt]);

  useEffect(() => {
    let active = true;
    getRemainingUsage(clientId, newsletterTest)
      .then((usage) => {
        if (active) {
          setRemainingUses(usage.remaining);
          setDailyLimit(usage.limit);
          setRequiresSubscription(usage.requiresSubscription);
        }
      })
      .catch(() => {
        if (active) setRemainingUses(undefined);
      });

    return () => {
      active = false;
    };
  }, [clientId, newsletterTest]);

  async function downloadFinalImage(force = false) {
    if (!downloadId) return;
    const downloadKey = `portrait_download_started:${downloadId}`;
    if (!force && window.sessionStorage.getItem(downloadKey)) {
      setDownloadMessage('Your download was already started. Use the button below if you need to download the file again.');
      return;
    }

    setDownloadMessage('Preparing your portrait download...');
    setError('');

    try {
      const downloadToken = new URLSearchParams(window.location.search).get('token') || window.sessionStorage.getItem(`portrait_access_token:${downloadId}`) || '';
      const preview = await getPreview(downloadId, downloadToken, newsletterTest);
      if (!preview.downloadUrl) {
        throw new Error('Final image is not ready yet. Please try again in a moment.');
      }

      await downloadImage(preview.downloadUrl, `portrait-${downloadId}.jpg`);
      window.sessionStorage.setItem(downloadKey, '1');
      setDownloadMessage('Your download has started. You can close this page after the file is saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download your portrait.');
      setDownloadMessage('We could not start the download automatically.');
    }
  }

  useEffect(() => {
    if (!downloadId) return;
    void downloadFinalImage();
  }, [downloadId]);

  function submit() {
    if (requiresSubscription) {
      setShowNewsletter(true);
      return;
    }
    void createPreview();
  }

  async function createPreview() {
    if (
      !humanImages.every(isFile) ||
      !petImages.every(isFile)
    ) return;

    setError('');
    setSubmitting(true);
    setProcessingStartedAt(Date.now());
    setProgressTick(0);
    setProcessingStage('analyzing');
    setProcessingMessage('Analyzing your photos...');

    try {
      const result = await createGeneration({
        humanImages: humanImages.filter(isFile),
        petImages: petImages.filter(isFile),
        mood,
        clothingStyle,
        numberOfPersons,
        numberOfPets,
        clientId,
        rootId: uploadRootId,
        accessToken: uploadRootToken,
        trademark: config.trademark,
        newsletterTest
      });
      if (typeof result.remainingUses === 'number') {
        setRemainingUses(result.remainingUses);
      }
      if (typeof result.requiresSubscription === 'boolean') {
        setRequiresSubscription(result.requiresSubscription);
      }
      rememberAccessToken(result.id, result.accessToken);
      rememberAccessToken(result.rootId || result.id, result.accessToken);
      rememberActiveRoot(result.rootId || result.id, result.accessToken);
      setProcessingStage('queued');
      setProcessingMessage('Queued for portrait generation...');

      for (let attempt = 0; attempt < 180; attempt += 1) {
        const preview = await getPreview(result.id, result.accessToken, newsletterTest);
        const imageReady = preview.previewUrl ? await canLoadImage(preview.previewUrl) : false;
        if (preview.status === 'queued') {
          setProcessingStage('queued');
          setProcessingMessage('Queued for portrait generation...');
        }
        if (preview.status === 'processing') {
          setProcessingStage(preview.previewUrl ? 'finalizing' : 'processing');
          setProcessingMessage(preview.previewUrl ? 'Finalizing your preview image...' : 'Creating your portrait...');
        }
        if (preview.status === 'completed') {
          if (!imageReady) {
            setProcessingStage('finalizing');
            setProcessingMessage('Finalizing your preview image...');
            await new Promise((resolve) => window.setTimeout(resolve, 1000));
            continue;
          }
          setProcessingStage('completed');
          window.location.href = previewPath(result.id, result.accessToken, newsletterTest);
          return;
        }
        if (preview.status === 'failed') {
          setProcessingStage('failed');
          throw new Error(preview.errorMessage || 'Generation failed.');
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
      }

      window.location.href = previewPath(result.id, result.accessToken, newsletterTest);
    } catch (err) {
      if (isApiErrorCode(err, 'NEWSLETTER_SUBSCRIPTION_REQUIRED')) {
        setRequiresSubscription(true);
        setShowNewsletter(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not create your preview.');
      }
      setSubmitting(false);
      setProcessingMessage('');
      setProcessingStage('analyzing');
      setProcessingStartedAt(undefined);
    }
  }

  function scrollDemo(direction: -1 | 1) {
    const strip = demoStripRef.current;
    if (!strip) return;
    const gap = Number.parseFloat(window.getComputedStyle(strip).columnGap || '0') || 0;
    strip.scrollBy({ left: direction * (strip.clientWidth + gap), behavior: 'smooth' });
  }

  if (isSubmitting && processingMessage) {
    const progress = estimatePortraitProgress(processingStartedAt, processingStage, progressTick);
    return <ProcessingView message={processingMessage} progress={progress} />;
  }

  if (downloadId) {
    return (
      <DownloadView
        error={error}
        message={downloadMessage || 'Preparing your portrait download...'}
        onDownloadAgain={() => void downloadFinalImage(true)}
      />
    );
  }

  return (
    <div className="upload-flow">
      <NewsletterModal
        clientId={clientId}
        open={showNewsletter}
        onClose={() => setShowNewsletter(false)}
        onSubscribed={() => {
          setRequiresSubscription(false);
          setShowNewsletter(false);
          void createPreview();
        }}
      />
      <header className="hero-copy upload-hero-copy">
        <h1>
          Together in a Moment
          <br />
          That Lasts <em>Forever</em>
        </h1>
        <img
          alt="A sample transformation from separate person and pet photos into one portrait"
          className="hero-sample"
          src={sampleAsset('mix-banner.webp')}
        />
      </header>

      <section className="step-section upload-step-section">
        <p className="upload-intro">
          Bring everyone you love together in one timeless portrait. Upload one clear photo for each person and pet you would like to include.
        </p>
        <h2>
          <span className="step-number">1</span>
          <span className="step-title">
            <em>Upload</em> Your Photos
          </span>
        </h2>
        <div className="upload-groups">
          <div className="upload-group-card">
            <div className="upload-drop-grid">
              {humanImages.map((image, index) => (
                <FileDrop
                  key={`person-${index}`}
                  label={`Photo of Person ${index + 1}`}
                  value={image}
                  onChange={(file) => setHumanImageAt(index, file)}
                  onRemove={image || index > 0 ? () => removeHumanImageAt(index) : undefined}
                  removeLabel={`Remove photo of person ${index + 1}`}
                />
              ))}
            </div>
            {humanImages.every(isFile) && (
              <div className="add-subject-panel">
                <p>
                  {numberOfPersons < MAX_SUBJECTS_PER_GROUP
                    ? 'Would you like to include another special person in your portrait?'
                    : 'All person spots have been added to your portrait.'}
                </p>
                {numberOfPersons < MAX_SUBJECTS_PER_GROUP && (
                  <>
                    <span>Add another person, or continue below to choose a Color Mood and view your demo.</span>
                    <button type="button" className="add-subject-button" onClick={addHumanImage}>
                      <span aria-hidden="true">+</span> Add Another Person
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="upload-group-card">
            <div className="upload-drop-grid">
              {petImages.map((image, index) => (
                <FileDrop
                  key={`pet-${index}`}
                  label={`Photo of Pet ${index + 1}`}
                  value={image}
                  onChange={(file) => setPetImageAt(index, file)}
                  onRemove={image || index > 0 ? () => removePetImageAt(index) : undefined}
                  removeLabel={`Remove photo of pet ${index + 1}`}
                />
              ))}
            </div>
            {petImages.every(isFile) && (
              <div className="add-subject-panel">
                <p>
                  {numberOfPets < MAX_SUBJECTS_PER_GROUP
                    ? 'Is there another beloved pet you would like to include?'
                    : 'All pet spots have been added to your portrait.'}
                </p>
                {numberOfPets < MAX_SUBJECTS_PER_GROUP && (
                  <>
                    <span>Add another pet, or continue below to choose a Color Mood and view your demo.</span>
                    <button type="button" className="add-subject-button" onClick={addPetImage}>
                      <span aria-hidden="true">+</span> Add Another Pet
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="hint">*Clear faces, no cropping and good lighting give the best results.</p>
      </section>

      <section className="step-section">
        <h2>
          <span className="step-number">2</span>
          <span className="step-title">
            <em>Choose</em> a Color Mood
          </span>
        </h2>
        <div className="choice-grid mood-grid">
          {moodOptions.map((option) => (
            <ChoiceCard
              image={option.image}
              key={option.id}
              label={option.label}
              onClick={() => setMood(option.id)}
              selected={mood === option.id}
            />
          ))}
        </div>
      </section>

      {error && <p className="form-error">{error}</p>}
      <p className={`usage-status ${hasRemainingUses ? '' : 'is-empty'}`}>
        {remainingUses === undefined
          ? dailyLimit === undefined
            ? 'Checking today\'s demo availability...'
            : `You can view up to ${dailyLimit} ${dailyLimit === 1 ? 'demo' : 'demos'} per day.`
          : remainingUses > 0
            ? `${remainingUses} demo ${remainingUses === 1 ? 'view' : 'views'} remaining today.`
            : dailyLimit === undefined
              ? 'You have used all demo views today. Please come back tomorrow.'
              : `You have used all ${dailyLimit} demo ${dailyLimit === 1 ? 'view' : 'views'} today. Please come back tomorrow.`}
      </p>
      <button className={`primary-action ${canSubmit ? 'is-active' : ''}`} disabled={!canSubmit} type="button" onClick={submit}>
        {isSubmitting ? 'Creating Your Demo...' : hasRemainingUses ? 'View Demo' : 'Daily Demo Limit Reached'}
      </button>

      <div className="demo-carousel">
        <button className="demo-nav demo-nav-prev" type="button" aria-label="Previous demo" onClick={() => scrollDemo(-1)}>
          ‹
        </button>
        <div className="demo-strip" ref={demoStripRef}>
          {demoImages.map((image) => (
            <img alt={image.alt} key={image.src} src={image.src} />
          ))}
        </div>
        <button className="demo-nav demo-nav-next" type="button" aria-label="Next demo" onClick={() => scrollDemo(1)}>
          ›
        </button>
      </div>

      <Reviews config={config.reviews} variant="summary" />
    </div>
  );
}

async function downloadImage(url: string, filename: string) {
  try {
    const response = await fetch(url);
    if (!response.ok || !(response.headers.get('content-type') || '').startsWith('image/')) {
      throw new Error('Could not download the final image.');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1200);
  } catch {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
